import { useMemo } from 'react';

import { useAuthContext } from './Auth';

type TenantInfo = {
    tenantGUID: string;
};

const getSelectedTenantGUIDs = (selectedTenants: TenantInfo[]) => {
    const tenantGUIDs = selectedTenants.map((tenant) => tenant.tenantGUID);

    return tenantGUIDs.filter(
        (tenantGUID, index, values) => Boolean(tenantGUID) && values.indexOf(tenantGUID) === index,
    );
};

export const useSelectedTenantGUIDs = () => {
    const { selectedTenants } = useAuthContext();

    return useMemo(() => getSelectedTenantGUIDs(selectedTenants), [selectedTenants]);
};

/**
 * Returns a short label describing the active tenant scope (e.g. "Acme" or
 * "3 client bases") for pages/filters that need to surface which tenants are in
 * view. Returns null when there's no meaningful scope to show (single-tenant
 * setup or all tenants selected).
 */
export const useTenantScopeLabel = () => {
    const { availableTenants, selectedTenants } = useAuthContext();

    if (availableTenants.length <= 1) {
        return null;
    }

    const selectedTenantCount = selectedTenants.length;

    if (selectedTenantCount === 0 || selectedTenantCount === availableTenants.length) {
        return null;
    }

    if (selectedTenantCount === 1) {
        return selectedTenants[0]?.name ?? null;
    }

    return `${selectedTenantCount} client bases`;
};
